import { constrainKartToOuterBand, resolveKartCollisions, stepKart } from "../game/simulation/kartPhysics";
import { capHiccupsSpeed, createRace } from "../game/simulation/race";
import { applyHiccupsInput, hasHiccupsTarget, predictLocalItemStep } from "../game/simulation/items";
import { stepDrafting } from "../game/simulation/drafting";
import { stepKartHazards } from "../game/simulation/hazards";
import { stepCourseEvents } from "../game/simulation/courseEvents";
import { DEFAULT_RACE_CLASS, normalizeRaceClass } from "../game/simulation/balance";
import type { DriverInput, KartState, RaceState } from "../game/simulation/types";
import { parseServerMessage, PROTOCOL_VERSION, type ServerMessage } from "./protocol";
import { inflateRaceSnapshot, RemoteInterpolator } from "./RemoteInterpolator";
import { CustomTrackDistributionError, CustomTrackRuntimeRegistry, isCustomTrackIdCandidate } from "./customTrackDistribution";
import { getClassicTrackRules } from "../game/simulation/track";
import { classicCompletedLaps } from "../game/simulation/classicProgress";

interface PendingInput {
  seq: number;
  input: DriverInput;
  /** Null until this sample has been put on the wire. */
  targetTick: number | null;
}

/**
 * Replay one unacknowledged local input against disposable copies of the last
 * authoritative world. This predicts contact that directly changes the chase
 * kart, but never consumes an authoritative hazard, advances the authoritative
 * event state, moves a remote kart, or invents a pickup/projectile on the
 * client. Incoming projectiles intentionally remain snapshot-owned.
 */
export function predictLocalRaceStep(race: RaceState, kart: KartState, input: DriverInput, dt: number, predictedElapsed: number): void {
  const predictionRace: RaceState = {
    ...race,
    elapsed: predictedElapsed,
    karts: race.karts.map((candidate) => candidate.id === kart.id ? kart : { ...candidate }),
    hazards: race.hazards.map((hazard) => ({ ...hazard })),
    courseEvent: race.courseEvent ? { ...race.courseEvent } : null,
  };

  stepDrafting(predictionRace, dt);
  predictLocalItemStep(kart, input, dt, hasHiccupsTarget(predictionRace, kart));
  const predictedInput = applyHiccupsInput(kart, input, predictedElapsed);
  capHiccupsSpeed(kart, race.raceClass);
  stepKart(kart, predictedInput, dt, race.trackId, race.raceClass);
  capHiccupsSpeed(kart, race.raceClass);
  stepKartHazards(predictionRace, kart, dt);
  const collisionKarts = resolveKartCollisions(predictionRace.karts, race.trackId);
  if (collisionKarts.has(kart)) {
    const projection = constrainKartToOuterBand(kart, race.trackId, false);
    if (!kart.airborne) kart.y = projection.position.y + 0.14;
  }
  stepCourseEvents(predictionRace, dt);
  const classic = getClassicTrackRules(race.trackId);
  kart.lap = classic ? Math.min(race.totalLaps, classicCompletedLaps(kart, classic) + 1) : Math.max(1, Math.floor(Math.max(0, kart.distance) / race.trackLength) + 1);
}

const SESSION_KEY = "beerkart-v2-session";
const DRIVER_KEY = "beerkart-v2-driver-name";
const COLOR_KEY = "beerkart-v2-kart-color";
const VARIANT_KEY = "beerkart-v2-kart-variant";
const LIVERY_KEY = "beerkart-v2-kart-livery";
const ACCENT_KEY = "beerkart-v2-kart-accent";
const WHEEL_KEY = "beerkart-v2-kart-wheels";
const DRIVER_COLOR_KEY = "beerkart-v2-driver-color";
const WEIGHT_KEY = "beerkart-v2-kart-weight";
const PATTERN_KEY = "beerkart-v2-kart-pattern";
const TOPPER_KEY = "beerkart-v2-kart-topper";
const STICKER_KEY = "beerkart-v2-kart-sticker";
const UNDERGLOW_KEY = "beerkart-v2-kart-underglow";
const HORN_KEY = "beerkart-v2-kart-horn";
const SKID_KEY = "beerkart-v2-kart-skid";
const TRAIL_KEY = "beerkart-v2-kart-trail";
const NUMBER_KEY = "beerkart-v2-kart-number";
const RACE_CLASS_KEY = "beerkart-v2-race-class";

// The server accepts targets at most 12 ticks ahead. Keep four ticks of
// margin because the latest snapshot is itself delayed by transport and the
// server may be briefly behind its interval while doing simulation work.
const MAX_INPUT_TARGET_LEAD = 8;
const MAX_PENDING_INPUTS = 120;
// Snapshots normally arrive every 50 ms. After a long browser/compile pause,
// the last server tick is no longer a safe scheduling base (the server rejects
// inputs more than 30 ticks late). Wait for authority instead of emitting a
// catch-up burst against that stale clock; do not extrapolate its tick.
const MAX_INPUT_SNAPSHOT_AGE_MS = 250;
const RECOVERY_PING_RETRY_MS = 1_000;

/** Status shown while a live-race connection waits for the next grid. */
export const QUEUED_STATUS = "SPECTATING / JOINING NEXT RACE";
export const CUSTOM_TRACK_WAITING_STATUS = "WAITING FOR TRACK";
export const CUSTOM_TRACK_INVALID_STATUS = "CUSTOM_TRACK_INVALID";

export interface AutopilotDiagnostics {
  wanted: boolean;
  enabled: boolean;
  pending: boolean;
  available: boolean;
}

export class NetworkRaceClient {
  constructor(private readonly role: "player" | "overlay" = "player") {}
  onStateChange: (() => void) | null = null;
  state: RaceState = createRace(0);
  localKartId: string | null = null;
  connected = false;
  status = "OFFLINE";
  presentationEpoch = 0;
  private socket: WebSocket | null = null;
  private inputSeq = 0;
  private serverTick = 0;
  private lastAuthoritativeClockAt = Number.NEGATIVE_INFINITY;
  private recoveryPing: number | null = null;
  private recoveryPingAt = Number.NEGATIVE_INFINITY;
  private recoveryPingSeq = 0;
  private nextInputTargetTick = 1;
  /** Stop sending until a fresh authoritative snapshot gives us a new base. */
  private inputRecoveryPending = false;
  private readonly pending: PendingInput[] = [];
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private manuallyClosed = false;
  private welcomed = false;
  private readonly interpolator = new RemoteInterpolator();
  private readonly customTracks = new CustomTrackRuntimeRegistry();
  private readonly pendingCustomSnapshots = new Map<string, Extract<ServerMessage, { type: "snapshot" }>>();
  private pendingCustomTrackId: string | null = null;
  private trackPreparationPending = false;
  private autopilotWanted = false;
  private autopilotEnabled = false;
  private autopilotPending = false;
  /** True once the app/user has explicitly selected a desired mode. */
  private autopilotIntentExplicit = false;
  private lastAutopilotRequest: boolean | null = null;

  connect(): void {
    if (this.socket && (this.socket.readyState === WebSocket.OPEN || this.socket.readyState === WebSocket.CONNECTING)) return;
    this.manuallyClosed = false;
    this.status = "CONNECTING";
    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${scheme}//${location.host}/ws`);
    this.socket = socket;
    socket.addEventListener("open", () => this.hello());
    socket.addEventListener("message", (event) => this.receive(String(event.data)));
    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.connected = false;
      this.clearAvatarIdentities();
      this.pendingCustomSnapshots.clear();
      this.pendingCustomTrackId = null;
      this.trackPreparationPending = false;
      this.autopilotPending = this.autopilotIntentExplicit && this.autopilotWanted !== this.autopilotEnabled;
      this.status = this.isQueued ? QUEUED_STATUS : "RECONNECTING";
      if (!this.manuallyClosed) this.scheduleReconnect();
    });
    socket.addEventListener("error", () => { /* close drives retry */ });
  }

  close(): void {
    this.manuallyClosed = true;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
    this.pendingCustomSnapshots.clear();
    this.pendingCustomTrackId = null;
    this.trackPreparationPending = false;
    this.socket?.close(1000, "client closed");
  }

  /**
   * Leave the authoritative race deliberately. A normal close is reserved for
   * transient disconnects and therefore keeps the resumable session alive;
   * this path tells the server to release the slot immediately and discards
   * the browser's resume credential so a later visit starts a fresh session.
   */
  leave(): void {
    this.manuallyClosed = true;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;

    const socket = this.socket;
    if (this.role === "player" && this.welcomed && socket?.readyState === WebSocket.OPEN) this.send({ type: "leave" });
    try { localStorage.removeItem(SESSION_KEY); } catch { /* storage may be unavailable in a private browser context */ }

    this.socket = null;
    this.connected = false;
    this.welcomed = false;
    this.localKartId = null;
    this.inputSeq = 0;
    this.serverTick = 0;
    this.lastAuthoritativeClockAt = Number.NEGATIVE_INFINITY;
    this.recoveryPing = null;
    this.nextInputTargetTick = 1;
    this.inputRecoveryPending = false;
    this.pending.length = 0;
    this.interpolator.reset();
    // Do not leave a custom state pointing at a registration that was just
    // released; the offline UI must always have a valid built-in fallback.
    this.state = createRace(0);
    this.customTracks.clear();
    this.pendingCustomSnapshots.clear();
    this.pendingCustomTrackId = null;
    this.trackPreparationPending = false;
    this.autopilotWanted = false;
    this.autopilotEnabled = false;
    this.autopilotPending = false;
    this.autopilotIntentExplicit = false;
    this.lastAutopilotRequest = null;
    this.status = "OFFLINE";
    this.onStateChange?.();
    socket?.close(1000, "player left");
  }

  step(input: DriverInput): void {
    if (this.role === "overlay") return;
    if (!this.connected || !this.localKartId || this.trackPreparationPending) return;
    // Once autopilot is requested, active, or awaiting the OFF acknowledgement,
    // do not enqueue or predict manual samples. The authoritative snapshots
    // remain the sole source of local motion until the handoff completes.
    if (this.autopilotWanted || this.autopilotEnabled || this.autopilotPending) return;
    const seq = ++this.inputSeq;
    this.pending.push({ seq, input: { ...input }, targetTick: null });
    if (this.pending.length > MAX_PENDING_INPUTS) this.pending.splice(0, this.pending.length - MAX_PENDING_INPUTS);
    this.sendNextPendingInput();
    if (this.state.phase === "race" || this.state.phase === "results") {
      const local = this.state.karts.find((kart) => kart.id === this.localKartId);
      if (local && local.finishedAt === null) this.predictLocalStep(local, input, this.pending.length);
    }
  }

  get localKart(): KartState | null {
    return this.state.karts.find((kart) => kart.id === this.localKartId) ?? null;
  }

  /**
   * True after a welcome deliberately leaves this player off the live grid.
   * Keep the reservation identity through a brief reconnect so rendering can
   * never fall back to somebody else's kart while the socket is recovering.
   */
  get isQueued(): boolean {
    return this.role === "player" && this.welcomed && this.localKartId === null;
  }

  getRenderState(now = performance.now()): RaceState {
    return this.interpolator.sample(now, this.state, this.localKartId);
  }

  setAutopilot(enabled: boolean): void {
    if (this.role === "overlay") return;
    this.autopilotIntentExplicit = true;
    this.autopilotWanted = enabled;
    this.autopilotPending = this.autopilotWanted !== this.autopilotEnabled;
    this.clearPendingInputQueue();
    if (!this.autopilotPending) {
      this.lastAutopilotRequest = null;
      return;
    }
    this.sendAutopilotRequest(this.autopilotWanted);
    this.onStateChange?.();
  }

  getAutopilotDiagnostics(): AutopilotDiagnostics {
    return {
      wanted: this.autopilotWanted,
      enabled: this.autopilotEnabled,
      pending: this.autopilotPending,
      // A welcomed player may be queued for the next grid. The authenticated
      // session still owns the preference and the server accepts it before a
      // kart is assigned, so localKartId must not gate this capability.
      available: this.role === "player" && this.connected && this.welcomed && !this.trackPreparationPending,
    };
  }

  private hello(): void {
    if (this.role === "overlay") {
      this.send({ type: "hello", protocol: PROTOCOL_VERSION, role: "overlay" });
      return;
    }
    let resume: { sessionId?: string; reconnectToken?: string } = {};
    try { resume = JSON.parse(localStorage.getItem(SESSION_KEY) ?? "{}"); } catch { /* start fresh */ }
    const name = (localStorage.getItem(DRIVER_KEY) || "IAN").slice(0, 20);
    const color = Number.parseInt(localStorage.getItem(COLOR_KEY) || "f3c552", 16);
    const variant = Number(localStorage.getItem(VARIANT_KEY) || 0);
    const livery = Number(localStorage.getItem(LIVERY_KEY) || 0);
    const accentColor = Number.parseInt(localStorage.getItem(ACCENT_KEY) || "fff3c4", 16);
    const wheelStyle = Number(localStorage.getItem(WHEEL_KEY) || 0);
    const driverColor = Number.parseInt(localStorage.getItem(DRIVER_COLOR_KEY) || "2d7888", 16);
    const weightClass = localStorage.getItem(WEIGHT_KEY) === "light" || localStorage.getItem(WEIGHT_KEY) === "heavy" ? localStorage.getItem(WEIGHT_KEY) as "light" | "heavy" : "medium";
    const pattern = (localStorage.getItem(PATTERN_KEY) || "solid") as "solid" | "stripe" | "rally" | "split";
    const topper = (localStorage.getItem(TOPPER_KEY) || "none") as "none" | "cap" | "lime" | "crown" | "laurel";
    const sticker = (localStorage.getItem(STICKER_KEY) || "none") as "none" | "hop" | "bolt" | "pint" | "medal";
    const underglow = Number.parseInt(localStorage.getItem(UNDERGLOW_KEY) || "0", 16) || 0;
    const horn = (localStorage.getItem(HORN_KEY) || "toot") as "toot" | "lager" | "air" | "bell";
    const skidColor = Number.parseInt(localStorage.getItem(SKID_KEY) || "120c08", 16);
    const trail = (localStorage.getItem(TRAIL_KEY) || "bubbles") as "bubbles" | "flame" | "foam" | "hops" | "neon" | "stars" | "none";
    const raceNumber = (localStorage.getItem(NUMBER_KEY) || "").slice(0, 3).toUpperCase();
    const raceClass = normalizeRaceClass(localStorage.getItem(RACE_CLASS_KEY) || DEFAULT_RACE_CLASS);
    this.send({ type: "hello", protocol: PROTOCOL_VERSION, role: "player", name, color, accentColor, variant, livery, wheelStyle, driverColor, weightClass, pattern, topper, sticker, underglow, horn, skidColor, trail, raceNumber, raceClass, ...resume });
  }

  private receive(raw: string): void {
    let message: ServerMessage | null;
    try { message = parseServerMessage(raw); } catch (error) {
      // A descriptor is the only server payload with a strict schema at this
      // layer. Treat malformed data as a typed connection state rather than
      // allowing a bad custom course to reach track/render code.
      this.status = error instanceof CustomTrackDistributionError ? error.code : CUSTOM_TRACK_INVALID_STATUS;
      this.onStateChange?.();
      return;
    }
    if (!message) return;
    if (message.type === "custom-track-definition") {
      this.receiveCustomTrackDefinition(message);
      return;
    }
    if (message.type === "welcome") {
      // The server re-sends welcome on this socket when a queued session is
      // promoted or a rematch changes its kart assignment.  Its input
      // sequence is per socket, so only the first welcome for a live
      // connection may rebase the client sequence.  `welcomed` intentionally
      // survives a transport close so the queued UI can retain its identity;
      // `connected` is the lifecycle boundary that distinguishes a fresh
      // socket from a repeated assignment notification.
      const freshConnection = !this.connected;
      this.welcomed = true;
      this.connected = true;
      this.status = this.connectedStatus(message.localKartId);
      this.reconnectAttempts = 0;
      this.localKartId = message.localKartId;
      this.serverTick = message.serverTick;
      this.lastAuthoritativeClockAt = performance.now();
      this.recoveryPing = null;
      this.nextInputTargetTick = message.serverTick + 1;
      this.inputRecoveryPending = false;
      if (freshConnection) this.inputSeq = 0;
      this.pending.length = 0;
      const authoritativeAutopilot = message.autopilot === true;
      this.autopilotEnabled = authoritativeAutopilot;
      if (!this.autopilotIntentExplicit) this.autopilotWanted = authoritativeAutopilot;
      this.autopilotPending = this.autopilotIntentExplicit && this.autopilotWanted !== authoritativeAutopilot;
      this.lastAutopilotRequest = null;
      this.pendingCustomSnapshots.clear();
      this.pendingCustomTrackId = null;
      this.trackPreparationPending = false;
      this.interpolator.reset();
      // A repeated welcome is an assignment/rematch boundary. Do not keep
      // presentation identities from the previous grid while its first fresh
      // snapshot is still in flight.
      this.clearAvatarIdentities();
      this.presentationEpoch += 1;
      if (this.role === "player" && message.sessionId && message.reconnectToken) localStorage.setItem(SESSION_KEY, JSON.stringify({ sessionId: message.sessionId, reconnectToken: message.reconnectToken }));
      if (this.autopilotPending) this.sendAutopilotRequest(this.autopilotWanted);
      this.onStateChange?.();
      return;
    }
    if (message.type === "snapshot") {
      if (!this.isCustomTrackReady(message.state.trackId)) {
        this.queueCustomSnapshot(message);
        return;
      }
      this.applySnapshot(message);
      return;
    }
    if (message.type === "autopilot-state") {
      this.receiveAutopilotState(message.enabled);
      return;
    }
    if (message.type === "pong" && message.sentAt === this.recoveryPing) {
      this.recoveryPing = null;
      // Keep input suspended until a snapshot sent after this marker arrives.
      this.lastAuthoritativeClockAt = performance.now();
      return;
    }
    if (message.type === "error" && !message.fatal && (message.code === "INPUT_FUTURE" || message.code === "INPUT_STALE")) {
      // Do not keep emitting packets that the server must reject. Waiting for
      // one snapshot also bounds the number of retries and lets the server's
      // own anti-abuse sequence/target checks remain authoritative.
      this.inputRecoveryPending = true;
      this.nextInputTargetTick = this.serverTick + 1;
      return;
    }
    if (message.type === "error" && message.fatal) {
      if (message.code === "RESUME_INVALID") {
        localStorage.removeItem(SESSION_KEY);
        this.clearAvatarIdentities();
      }
      this.status = message.code;
      this.onStateChange?.();
    }
  }

  private connectedStatus(localKartId = this.localKartId): string {
    return this.role === "overlay" || localKartId !== null ? "CONNECTED" : QUEUED_STATUS;
  }

  private isCustomTrackReady(trackId: string): boolean {
    return !isCustomTrackIdCandidate(trackId) || this.customTracks.has(trackId);
  }

  private queueCustomSnapshot(message: Extract<ServerMessage, { type: "snapshot" }>): void {
    const trackId = message.state.trackId;
    this.pendingCustomSnapshots.set(trackId, message);
    while (this.pendingCustomSnapshots.size > 8) this.pendingCustomSnapshots.delete(this.pendingCustomSnapshots.keys().next().value!);
    this.pendingCustomTrackId = trackId;
    this.trackPreparationPending = true;
    this.status = CUSTOM_TRACK_WAITING_STATUS;
    this.onStateChange?.();
  }

  private receiveCustomTrackDefinition(message: Extract<ServerMessage, { type: "custom-track-definition" }>): void {
    try {
      const descriptor = this.customTracks.register(message);
      const pending = this.pendingCustomSnapshots.get(descriptor.runtimeId);
      if (!pending || this.pendingCustomTrackId !== descriptor.runtimeId) return;
      this.pendingCustomSnapshots.delete(descriptor.runtimeId);
      this.pendingCustomTrackId = null;
      this.trackPreparationPending = false;
      this.applySnapshot(pending);
    } catch (error) {
      const distributionError = error instanceof CustomTrackDistributionError ? error : null;
      this.status = distributionError?.code ?? CUSTOM_TRACK_INVALID_STATUS;
      this.onStateChange?.();
    }
  }

  private applySnapshot(message: Extract<ServerMessage, { type: "snapshot" }>): boolean {
      // Receiving a queued snapshot is not evidence that its clock is fresh.
      // After a main-thread pause, round-trip a marker through the ordered
      // socket before allowing a subsequent snapshot to resume input.
      if (this.role === "player" && Number.isFinite(this.lastAuthoritativeClockAt)
        && performance.now() - this.lastAuthoritativeClockAt > MAX_INPUT_SNAPSHOT_AGE_MS) this.beginClockRecovery();
      // `serverTick` is an authoritative clock, never a local frame counter.
      // A stalled server must be allowed to move this value backwards relative
      // to a previously over-optimistic client estimate; keeping Math.max here
      // is what made a future-target rejection permanent.
      this.serverTick = message.tick;
      this.lastAuthoritativeClockAt = performance.now();
      if (message.lastAppliedInputSeq !== null) {
        while (this.pending.length && this.pending[0].seq <= message.lastAppliedInputSeq) this.pending.shift();
      }
      if (!this.interpolator.push(message.tick, message.state)) {
        if (isCustomTrackIdCandidate(message.state.trackId)) this.queueCustomSnapshot(message);
        else {
          this.status = "SNAPSHOT_INVALID";
          this.onStateChange?.();
        }
        return false;
      }
      try {
        this.state = inflateRaceSnapshot(message.state);
      } catch {
        this.status = "SNAPSHOT_INVALID";
        this.onStateChange?.();
        return false;
      }
      this.customTracks.observeTrack(message.state.trackId);
      this.pendingCustomSnapshots.clear();
      this.pendingCustomTrackId = null;
      this.trackPreparationPending = false;
      const authoritativeField = this.localKart?.autopilot;
      const authoritativeAutopilot = authoritativeField === true;
      if (typeof authoritativeField === "boolean" && authoritativeAutopilot !== this.autopilotEnabled) {
        this.autopilotEnabled = authoritativeAutopilot;
        this.autopilotPending = this.autopilotIntentExplicit && this.autopilotWanted !== authoritativeAutopilot;
        this.clearPendingInputQueue();
      } else if (typeof authoritativeField === "boolean" && this.autopilotPending && authoritativeAutopilot === this.autopilotWanted) {
        this.autopilotEnabled = authoritativeAutopilot;
        this.autopilotPending = false;
        this.lastAutopilotRequest = null;
        this.clearPendingInputQueue();
      }
      if (this.status === CUSTOM_TRACK_WAITING_STATUS || this.status === CUSTOM_TRACK_INVALID_STATUS) this.status = this.connectedStatus();
      const recovering = this.inputRecoveryPending;
      const minimumTargetTick = message.tick + 1;
      const maximumTargetTick = message.tick + MAX_INPUT_TARGET_LEAD;
      if (recovering && this.recoveryPing === null) {
        // A future/stale denial gives no sequence number, so the only safe
        // acknowledgement boundary is this fresh snapshot. Inputs still in
        // the local replay window were either rejected or superseded by the
        // authoritative state; discard them instead of replaying a second
        // multi-second speculative run. The next step starts a new sequence
        // on the rebased tick and inputSeq itself remains monotonic.
        this.pending.length = 0;
        this.inputRecoveryPending = false;
        this.nextInputTargetTick = minimumTargetTick;
      } else if (this.nextInputTargetTick < minimumTargetTick || this.nextInputTargetTick > maximumTargetTick + 1) {
        // Rebase after a delayed snapshot jumps beyond the old scheduling
        // window. `+1` is the sentinel used while the horizon is full.
        this.nextInputTargetTick = minimumTargetTick;
      }
      const local = this.localKart;
      if (!recovering && !this.autopilotWanted && !this.autopilotEnabled && !this.autopilotPending
        && local && local.finishedAt === null && (this.state.phase === "race" || this.state.phase === "results")) {
        for (let index = 0; index < this.pending.length; index += 1) this.predictLocalStep(local, this.pending[index].input, index + 1);
      }
      this.onStateChange?.();
      return true;
  }

  private send(value: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(value));
  }

  private sendAutopilotRequest(enabled: boolean): void {
    if (this.role === "overlay" || !this.connected || !this.welcomed || !this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    if (this.lastAutopilotRequest === enabled) return;
    this.lastAutopilotRequest = enabled;
    this.send({ type: "autopilot", enabled });
  }

  private receiveAutopilotState(enabled: boolean): void {
    const changed = enabled !== this.autopilotEnabled;
    this.autopilotEnabled = enabled;
    this.autopilotPending = this.autopilotIntentExplicit && this.autopilotWanted !== enabled;
    this.clearPendingInputQueue();
    if (!this.autopilotPending) this.lastAutopilotRequest = null;
    else this.sendAutopilotRequest(this.autopilotWanted);
    if (changed || !this.autopilotPending) this.onStateChange?.();
  }

  private clearPendingInputQueue(): void {
    this.pending.length = 0;
    this.inputRecoveryPending = false;
    this.nextInputTargetTick = this.serverTick + 1;
  }

  /** Never present a previous grid's avatar while disconnected/reassigning. */
  private clearAvatarIdentities(): void {
    // The renderer normally consumes getRenderState(), which is backed by a
    // separate interpolation cache. Reset it together with the authoritative
    // state so a transport close or invalid resume cannot keep drawing the
    // prior grid's identity during reconnect.
    this.interpolator.reset();
    for (const kart of this.state.karts) delete kart.avatarLogin;
  }

  /**
   * Put at most one unqueued local sample on the wire per simulation step.
   * Targets advance only inside the latest authoritative eight-tick window;
   * when that window is full, additional samples share its final tick until a
   * newer snapshot advances the window. The server applies the newest sample
   * for a due tick, which is the desired behavior for held controls and keeps
   * a stalled server from receiving INPUT_FUTURE storms.
   */
  private sendNextPendingInput(): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    if (this.inputRecoveryPending) {
      if (this.recoveryPing !== null) this.beginClockRecovery();
      return;
    }
    if (performance.now() - this.lastAuthoritativeClockAt > MAX_INPUT_SNAPSHOT_AGE_MS) {
      this.beginClockRecovery();
      return;
    }
    const pending = this.pending.find((entry) => entry.targetTick === null);
    if (!pending) return;
    const minimumTargetTick = this.serverTick + 1;
    const maximumTargetTick = this.serverTick + MAX_INPUT_TARGET_LEAD;
    if (this.nextInputTargetTick < minimumTargetTick || this.nextInputTargetTick > maximumTargetTick + 1) this.nextInputTargetTick = minimumTargetTick;
    const targetTick = Math.min(this.nextInputTargetTick, maximumTargetTick);
    pending.targetTick = targetTick;
    this.nextInputTargetTick = targetTick + 1;
    this.send({ type: "input", seq: pending.seq, targetTick, input: pending.input });
  }

  private beginClockRecovery(): void {
    this.inputRecoveryPending = true;
    // The server intentionally drops outbound messages under backpressure,
    // including pongs. A lost marker must not disable controls indefinitely.
    const now = performance.now();
    if (this.recoveryPing !== null && now - this.recoveryPingAt < RECOVERY_PING_RETRY_MS) return;
    this.recoveryPing = ++this.recoveryPingSeq;
    this.recoveryPingAt = now;
    this.send({ type: "ping", sentAt: this.recoveryPing });
  }

  private predictLocalStep(kart: KartState, input: DriverInput, pendingStep: number): void {
    const dt = 1 / 60;
    predictLocalRaceStep(this.state, kart, input, dt, this.state.elapsed + pendingStep * dt);
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const delay = Math.min(5_000, 250 * 2 ** this.reconnectAttempts++);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}
